<script setup lang="ts">
/**
 * Поля релиза: репозиторий, версия, описание — макет 92:1400.
 *
 * Одна форма на два места: в карточке задачи она живёт в модалке
 * (ReleaseDialog), на экране раздела стоит колонкой справа. Логика у них одна,
 * и держать её в двух файлах значило бы чинить проверку версии дважды.
 *
 * Список репозиториев — подсказка: у карточки его отдаёт сервис по эпику, у
 * экрана он собирается из истории. Решает человек, поэтому поле остаётся
 * вводимым.
 *
 * Тег ставится на master: в проекте релиз — это тег на уже влитом коммите, и
 * возможность собрать релиз с произвольной ветки приглашала бы выкатить
 * непроверенное.
 *
 * Описание — редактор, а не textarea (DEV-1624): оно хранится и читается как
 * markdown, а набор кнопок урезан до списков, начертаний, ссылки и кода —
 * картинок и таблиц в описании релиза не бывает.
 */
import { AppButton, AppSelect, AppTextField } from '@vinprop/design-system'
import { AppRichTextEditor } from '@vinprop/design-system/editor'
import { Sparkles } from 'lucide-vue-next'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { errorMessage } from '@/api/errors'

import { createRelease, generateReleaseNotes } from '../api'

/**
 * Набор кнопок редактора. Явный список, а не пресет: `issue` тащит картинки,
 * таблицы и полноэкранный режим, которым в описании релиза нечего делать, а
 * `comment` не знает нумерованных списков — а перечень того, что вошло, ими и
 * пишется.
 */
const DESCRIPTION_TOOLS = [
  'bold',
  'italic',
  'strike',
  'bulletList',
  'orderedList',
  'code',
  'link',
] as const

const props = defineProps<{
  services: string[]
  issueId?: string
  issueKey?: string
}>()

const emit = defineEmits<{ done: []; failed: [error: unknown] }>()

const { t } = useI18n()

const projectPath = ref(props.services[0] ?? '')
const tag = ref('')
const description = ref('')
const busy = ref(false)
const tagField = ref<HTMLElement | null>(null)

// Состояние генерации: подпись под полем о том, из чего собрано описание,
// ошибка сборки и вопрос о замене уже набранного текста.
const generating = ref(false)
const generated = ref<{ tag: string; commits: number } | null>(null)
const generateError = ref('')
const confirmingReplace = ref(false)

// Подсказки приезжают асинхронно: у карточки — запросом по эпику, у экрана —
// вместе с историей. Первый пришедший вариант и подставляем.
watch(
  () => props.services,
  (services) => {
    if (!projectPath.value) projectPath.value = services[0] ?? ''
  },
)

// Сменили репозиторий — подпись «собрано по 14 коммитам после v1.4.0» больше
// не про то описание, что в поле.
watch(projectPath, () => {
  generated.value = null
  generateError.value = ''
  confirmingReplace.value = false
})

const options = computed(() => props.services.map((path) => ({ value: path, label: path })))

/** Версия должна быть семвером с v: тег другого вида пайплайн релиза не ловит. */
const isValidTag = computed(() => /^v\d+\.\d+\.\d+$/.test(tag.value.trim()))

const isDescriptionEmpty = computed(() => description.value.trim() === '')

const submit = async () => {
  if (!projectPath.value || !isValidTag.value) return

  busy.value = true

  try {
    await createRelease(
      projectPath.value,
      tag.value.trim(),
      'master',
      description.value.trim() || props.issueKey || tag.value.trim(),
      props.issueId,
    )
    tag.value = ''
    description.value = ''
    generated.value = null
    emit('done')
  } catch (failure) {
    emit('failed', failure)
  } finally {
    busy.value = false
  }
}

/**
 * Кнопка «Сгенерировать». Набранный текст молча не затирается: он мог быть
 * написан руками полминуты назад, и второе нажатие по привычке стоило бы
 * человеку всего абзаца.
 */
const generate = () => {
  if (!isDescriptionEmpty.value) {
    confirmingReplace.value = true

    return
  }

  void replaceDescription()
}

const replaceDescription = async () => {
  confirmingReplace.value = false
  generating.value = true
  generateError.value = ''

  try {
    const notes = await generateReleaseNotes(projectPath.value)

    description.value = notes.description
    generated.value = { tag: notes.previousTag, commits: notes.commits }
  } catch (failure) {
    // Ошибка живёт в форме, а не в общем тосте: причина у неё конкретная —
    // «нет релизных тегов», «после тега ничего не приехало», «шлюз молчит», —
    // и читать её надо рядом с полем, которое осталось прежним.
    generateError.value = errorMessage(failure, t('devops.release.generateFailed'))
  } finally {
    generating.value = false
  }
}

// Кнопки бывают снаружи: в модалке они стоят в футере, а не в теле формы.
defineExpose({
  submit,
  busy,
  canSubmit: computed(() => Boolean(projectPath.value) && isValidTag.value),
  focus: () => tagField.value?.querySelector('input')?.focus(),
})
</script>

<template>
  <div class="rel">
    <p class="rel__hint">{{ t('devops.release.hint') }}</p>

    <AppSelect
      v-if="options.length"
      v-model="projectPath"
      :options="options"
      :label="t('devops.release.service')"
    />
    <AppTextField
      v-else
      v-model="projectPath"
      :label="t('devops.release.service')"
      placeholder="vinprop/backend/tracker"
    />

    <div ref="tagField">
      <AppTextField
        v-model="tag"
        :label="t('devops.release.tag')"
        placeholder="v1.4.0"
        :error="tag && !isValidTag ? t('devops.release.tagInvalid') : ''"
      />
    </div>

    <div class="rel__desc">
      <div class="rel__label">
        <span class="rel__caption">{{ t('devops.release.description') }}</span>
        <button
          type="button"
          class="rel__gen"
          :disabled="!projectPath || generating"
          @click="generate"
        >
          <Sparkles :size="14" />
          <span>{{ t('devops.release.generate') }}</span>
        </button>
      </div>

      <AppRichTextEditor
        v-model="description"
        :tools="[...DESCRIPTION_TOOLS]"
        min-height="120px"
        max-height="320px"
      />

      <!-- Замена уже набранного описания: вопрос стоит там же, где кнопка, и
           не уводит человека в модалку ради одного «да». -->
      <p v-if="confirmingReplace" class="rel__confirm">
        {{ t('devops.release.replaceTitle') }}
        <button type="button" class="rel__link" @click="replaceDescription">
          {{ t('devops.release.replace') }}
        </button>
        <button type="button" class="rel__link" @click="confirmingReplace = false">
          {{ t('common.actions.cancel') }}
        </button>
      </p>

      <p v-else-if="generating" class="rel__note">{{ t('devops.release.generating') }}</p>

      <p v-else-if="generateError" class="rel__note rel__note--error">{{ generateError }}</p>

      <p v-else-if="generated" class="rel__note">
        {{ t('devops.release.generated', { commits: generated.commits, tag: generated.tag }) }}
      </p>
    </div>

    <slot name="actions" :submit="submit" :busy="busy" :disabled="!projectPath || !isValidTag">
      <AppButton :loading="busy" :disabled="!projectPath || !isValidTag" @click="submit">
        {{ t('devops.release.create') }}
      </AppButton>
    </slot>
  </div>
</template>

<style scoped>
.rel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.rel__hint {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
}

.rel__desc {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.rel__label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.rel__caption {
  color: var(--color-text-secondary);
  font-size: var(--font-size-caption);
}

.rel__gen {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background-color: var(--color-surface);
  color: var(--color-primary);
  font-size: var(--font-size-caption);
  font-weight: 500;
  cursor: pointer;
}

.rel__gen:hover:not(:disabled) {
  background-color: var(--color-primary-tint);
}

.rel__gen:disabled {
  color: var(--color-text-disabled);
  cursor: not-allowed;
}

.rel__note,
.rel__confirm {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-caption);
}

.rel__note--error {
  color: var(--color-danger);
}

.rel__link {
  padding: 0;
  border: 0;
  background: none;
  color: var(--color-primary);
  font-size: inherit;
  cursor: pointer;
}
</style>
